/**
 * Mount Rigi product page
 *
 *   - variant pills: price, compare-at price and discount badge, button state,
 *     URL (?variant=) and the hero image follow the selection; values that are
 *     sold out in combination with the current choice are marked
 *   - add to cart over fetch, straight into the cart drawer; the form still
 *     posts to /cart/add without JavaScript
 *   - gallery lightbox on the native <dialog>: arrows, Escape, swipe
 */
(() => {
  'use strict';

  /* ---------- money ---------- */

  const formatNumber = (value, decimals, thousands = ',', separator = '.') => {
    const [whole, fraction] = value.toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (decimals ? separator + fraction : '');
  };

  const formatMoney = (cents, format = '${{amount}}') => {
    const value = Number(cents) / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return formatNumber(value, 0);
        case 'amount_with_comma_separator': return formatNumber(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatNumber(value, 0, '.', ',');
        default: return formatNumber(value, 2);
      }
    });
  };

  /* ---------- cart ---------- */

  const openCartDrawer = (state) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(state);
      return true;
    }
    return false;
  };

  /* ---------- product form ---------- */

  class MrProduct {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-mr-product-form]');
      if (!this.form) return;

      const data = root.querySelector('[data-mr-product-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.labels = {
        add: root.dataset.labelAdd || 'Add to cart',
        soldOut: root.dataset.labelSoldOut || 'Sold out',
        unavailable: root.dataset.labelUnavailable || 'Unavailable',
      };

      this.idInput = this.form.querySelector('input[name="id"]');
      this.submit = this.form.querySelector('[data-mr-product-submit]');
      this.submitLabel = this.form.querySelector('[data-mr-product-submit-label]');
      this.status = root.querySelector('[data-mr-product-status]');
      this.price = root.querySelector('[data-mr-product-price]');
      this.compare = root.querySelector('[data-mr-product-compare]');
      this.badge = root.querySelector('[data-mr-product-badge]');
      this.pickers = Array.from(root.querySelectorAll('[data-mr-option]'));

      this.pickers.forEach((picker) => picker.addEventListener('change', () => this.onChange()));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      this.markAvailability();
    }

    selectedOptions() {
      return this.pickers.map((picker) => picker.querySelector('input:checked')?.value);
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);

      this.pickers.forEach((picker, index) => {
        const current = picker.querySelector('[data-mr-option-current]');
        if (current) current.textContent = options[index] || '';
      });

      this.markAvailability();
      this.renderButton(variant);
      if (!variant) return;

      this.idInput.value = variant.id;
      this.renderPrice(variant);
      this.renderMedia(variant);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* A value counts as unavailable when no available variant carries it
       together with the values already chosen in the other options. */
    markAvailability() {
      const selected = this.selectedOptions();
      this.pickers.forEach((picker, index) => {
        picker.querySelectorAll('input').forEach((input) => {
          const candidate = [...selected];
          candidate[index] = input.value;
          const available = this.variants.some((variant) =>
            variant.available && variant.options.every((value, i) => value === candidate[i])
          );
          input.toggleAttribute('data-unavailable', !available);
        });
      });
    }

    renderPrice(variant) {
      if (this.price) this.price.textContent = formatMoney(variant.price, this.moneyFormat);

      const onSale = variant.available && variant.compare_at_price && variant.compare_at_price > variant.price;
      if (this.compare) {
        this.compare.hidden = !onSale;
        const value = this.compare.querySelector('[data-mr-product-compare-value]');
        if (onSale && value) value.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
      }
      if (this.badge) {
        this.badge.hidden = !onSale;
        if (onSale) {
          const percent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          const value = this.badge.querySelector('[data-mr-product-badge-value]');
          if (value) value.textContent = `-${percent}%`;
        }
      }
    }

    renderButton(variant) {
      if (!this.submit) return;
      let label = this.labels.add;
      let disabled = false;
      if (!variant) {
        label = this.labels.unavailable;
        disabled = true;
      } else if (!variant.available) {
        label = this.labels.soldOut;
        disabled = true;
      }
      this.submit.disabled = disabled;
      if (this.submitLabel) this.submitLabel.textContent = label;
    }

    /* The hero sits in another section; the data attribute is on its image
       wrapper, because image_tag cannot carry data-* attributes itself. */
    renderMedia(variant) {
      const media = variant.featured_media;
      const src = media?.preview_image?.src;
      if (!src) return;
      document.querySelectorAll('[data-mr-product-hero-image] img').forEach((image) => {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '1400');
        image.removeAttribute('srcset');
        image.src = url.toString();
        if (media.alt) image.alt = media.alt;
      });
    }

    async onSubmit(event) {
      if (!window.fetch) return;
      event.preventDefault();
      if (this.submit?.disabled || this.submit?.getAttribute('aria-busy') === 'true') return;

      const body = new FormData(this.form);
      body.append('sections', 'cart-drawer,cart-icon-bubble');
      body.append('sections_url', window.location.pathname);

      this.submit?.setAttribute('aria-busy', 'true');
      this.setStatus('');

      try {
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const state = await response.json();
        if (!response.ok) {
          this.setStatus(state?.description || state?.message || 'This item could not be added.', 'error');
          return;
        }
        if (!openCartDrawer(state)) window.location.assign(window.routes?.cart_url || '/cart');
      } catch (_) {
        this.form.submit();
      } finally {
        this.submit?.removeAttribute('aria-busy');
      }
    }

    setStatus(message, state) {
      if (!this.status) return;
      this.status.textContent = message || '';
      if (state) {
        this.status.dataset.state = state;
      } else {
        delete this.status.dataset.state;
      }
    }
  }

  /* ---------- lightbox ---------- */

  class MrLightbox {
    constructor(root) {
      this.dialog = root.querySelector('[data-mr-lightbox]');
      this.thumbs = Array.from(root.querySelectorAll('[data-mr-gallery-thumb]'));
      if (!this.dialog || this.thumbs.length === 0) return;

      this.image = this.dialog.querySelector('[data-mr-lightbox-image]');
      this.counter = this.dialog.querySelector('[data-mr-lightbox-counter]');
      this.prev = this.dialog.querySelector('[data-mr-lightbox-prev]');
      this.next = this.dialog.querySelector('[data-mr-lightbox-next]');
      this.index = 0;

      const single = this.thumbs.length < 2;
      if (this.prev) this.prev.hidden = single;
      if (this.next) this.next.hidden = single;

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.open(index, thumb));
      });
      this.prev?.addEventListener('click', () => this.show(this.index - 1));
      this.next?.addEventListener('click', () => this.show(this.index + 1));
      this.dialog.querySelector('[data-mr-lightbox-close]')?.addEventListener('click', () => this.close());

      // A click on the backdrop - outside the picture - closes the viewer.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.matches('[data-mr-lightbox-figure]')) this.close();
      });

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
        if (event.key === 'ArrowRight') this.show(this.index + 1);
      });

      this.dialog.addEventListener('close', () => this.opener?.focus());
      this.bindSwipe();
    }

    open(index, opener) {
      this.opener = opener;
      this.show(index);
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        window.open(this.thumbs[index].dataset.full, '_blank', 'noopener');
      }
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    show(index) {
      const count = this.thumbs.length;
      this.index = (index + count) % count;
      const thumb = this.thumbs[this.index];
      this.image.src = thumb.dataset.full;
      this.image.srcset = thumb.dataset.srcset || '';
      this.image.alt = thumb.dataset.alt || '';
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${count}`;
    }

    bindSwipe() {
      let startX = null;
      this.dialog.addEventListener('pointerdown', (event) => { startX = event.clientX; });
      this.dialog.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        startX = null;
        if (Math.abs(delta) < 48) return;
        this.show(this.index + (delta < 0 ? 1 : -1));
      });
    }
  }

  /* ---------- boot ---------- */

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-mr-product]').forEach((root) => {
      if (root.dataset.mrProductReady === 'true') return;
      root.dataset.mrProductReady = 'true';
      new MrProduct(root);
      new MrLightbox(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
